import 'css/src/components/sketch/ChatUploadDropdown.module.scss';

import React, { useCallback, useMemo, useRef } from 'react';
import Immutable from 'immutable';

import type { UploadType } from './FileUpload';

import { AssetTypes } from 'js/src/client';
import {
    BUTTON_SIZES,
    BUTTON_VARIANTS,
    TertiaryIconButton,
} from 'js/src/components/common/ui/Button';
import {
    Dropdown,
    DROPDOWN_DIVIDER,
    type DropdownItemProp,
    type DropdownRef,
} from 'js/src/components/common/ui/Dropdown';
import { type ImmutableMap, ImmutableFromJS } from 'js/src/libs/consts';

const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.bmp', '.webp', '.gif'];
const VIDEO_EXTENSIONS = ['.mov', '.mp4', '.avi', '.webm', '.m4v'];
const AUDIO_EXTENSIONS = ['.mp3', '.wav', '.m4a', '.aac', '.ogg'];
// All supported document types for general uploads
const DOCUMENT_EXTENSIONS = ['.pdf', '.docx', '.pptx', '.txt', '.csv', '.tsv', '.xml'];
// Text-based data formats only (for AI Charts which can't handle binary formats)
const TEXT_DATA_EXTENSIONS = ['.txt', '.csv', '.tsv'];

/** Matches Dropdown's default `myMenuPath`, which its `open()` expects. */
const DROPDOWN_MENU_PATH = 'menu';

/**
 * Highlights the attach item when the menu opens on the user's behalf, so the
 * one row that continues their flow is picked out of the list. Global rather
 * than a CSS module class because Bootstrap renders the menu outside this
 * component.
 */
const SWEEP_ITEM_CLASS = 'chat-upload-item--swept';

const UPLOAD_TYPE_ACCEPT_MAP: Record<UploadType, string> = {
    image: IMAGE_EXTENSIONS.join(','),
    video: VIDEO_EXTENSIONS.join(','),
    media: [...IMAGE_EXTENSIONS, ...VIDEO_EXTENSIONS].join(','),
    document: DOCUMENT_EXTENSIONS.join(','),
    textData: TEXT_DATA_EXTENSIONS.join(','),
    all: [
        ...DOCUMENT_EXTENSIONS,
        ...IMAGE_EXTENSIONS,
        ...VIDEO_EXTENSIONS,
        ...AUDIO_EXTENSIONS,
    ].join(','),
};

interface ChatUploadDropdownProps {
    onFilesSelected: (files: File[]) => void;
    disabled?: boolean;
    className?: string;
    uploadType?: UploadType;
    icon?: string;
    singleFile?: boolean;
    /** Override the auto-generated label (e.g. "Add image" → "Add a reference image"). */
    label?: string;
    /** When provided, adds a "Choose from your uploads" item that opens a
     * media-library picker instead of the OS file dialog. */
    onChooseExisting?: () => void;
    /** Extra menu items appended after the built-in ones, for surfaces that
     * start a flow from here (referencing a video, recording a screen). */
    extraItems?: ChatUploadDropdownItem[];
    ref?: React.Ref<ChatUploadDropdownRef>;
}

export interface ChatUploadDropdownItem {
    id: string;
    text: string;
    icon: string;
    onSelect: () => void;
}

/** Lets a caller outside the menu pop it open — see `ChatInputRef`. */
export interface ChatUploadDropdownRef {
    openMenu: () => void;
}

type UploadAction = { action: 'upload' | 'existing' | 'extra'; extraId?: string };

function CircularToggleButton({
    icon,
    ref,
    ...props
}: {
    onClick: () => void;
    disabled: boolean;
    icon: string;
    ref?: React.Ref<HTMLButtonElement>;
}) {
    return (
        <TertiaryIconButton
            {...props}
            ref={ref}
            variant={BUTTON_VARIANTS.OUTLINE}
            size={BUTTON_SIZES.MD}
            icon={icon}
        />
    );
}

export const ChatUploadDropdown: React.FC<ChatUploadDropdownProps> = ({
    onFilesSelected,
    disabled = false,
    className = '',
    uploadType = 'all',
    icon = 'add',
    singleFile = false,
    label: labelOverride,
    onChooseExisting,
    extraItems,
    ref,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<DropdownRef>(null);
    // The menu appearing without the user having clicked it needs pointing at,
    // so a programmatic open highlights the attach item once. Only that case —
    // a menu the user opened themselves is already where they are looking.
    const [sweepOnOpen, setSweepOnOpen] = React.useState(false);
    const hasOpenedRef = useRef(false);

    // The menu is opened from outside when a flow elsewhere leads here — a
    // "Start from" pill whose first step is attaching a file, say.
    React.useImperativeHandle(ref, () => ({
        openMenu: () => {
            setSweepOnOpen(true);
            // Deferred a tick: the click that asked for this is still
            // propagating, and Bootstrap's root-close listener would read it as
            // a click outside the menu and shut it again immediately.
            setTimeout(() => dropdownRef.current?.open(DROPDOWN_MENU_PATH), 0);
        },
    }));

    const handleFileSelection = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFileChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(event.target.files || []);
            if (files.length > 0) {
                onFilesSelected(files);
            }
            // Reset input to allow selecting same file again
            event.target.value = '';
        },
        [onFilesSelected],
    );

    const acceptString = useMemo(() => UPLOAD_TYPE_ACCEPT_MAP[uploadType], [uploadType]);

    const itemPropsList = useMemo(() => {
        let label = '';
        switch (uploadType) {
            case AssetTypes.IMAGE: {
                label = `Add image${singleFile ? '' : 's'}`;

                break;
            }
            case AssetTypes.VIDEO: {
                label = `Add video${singleFile ? '' : 's'}`;

                break;
            }
            case AssetTypes.DOCUMENT: {
                label = `Add document${singleFile ? '' : 's'}`;

                break;
            }
            case 'textData': {
                label = `Add data file${singleFile ? '' : 's'}`;

                break;
            }
            case 'media': {
                label = singleFile ? 'Add image or video' : 'Add images and videos';

                break;
            }
            case 'all': {
                label = singleFile ? 'Add photo or file' : 'Add photos and files';

                break;
            }
            // No default
        }

        const items: {
            id: string;
            text?: string;
            icon?: string;
            className?: string;
            onSelectData?: UploadAction;
        }[] = [
            {
                id: 'attach-files',
                text: labelOverride ?? label,
                icon: 'attach_file',
                className: sweepOnOpen ? SWEEP_ITEM_CLASS : undefined,
                onSelectData: { action: 'upload' },
            },
        ];
        if (onChooseExisting) {
            items.push({
                id: 'choose-existing',
                text: 'Choose from your uploads',
                icon: 'folder_open',
                onSelectData: { action: 'existing' },
            });
        }
        if (extraItems?.length) {
            // Separate uploading a file from the flows that start elsewhere.
            items.push({ id: DROPDOWN_DIVIDER });
        }
        for (const extra of extraItems ?? []) {
            items.push({
                id: extra.id,
                text: extra.text,
                icon: extra.icon,
                onSelectData: { action: 'extra', extraId: extra.id },
            });
        }

        return ImmutableFromJS(items) as Immutable.List<DropdownItemProp<UploadAction>>;
    }, [uploadType, singleFile, labelOverride, onChooseExisting, extraItems, sweepOnOpen]);

    const handleSelect = useCallback(
        (data: ImmutableMap<UploadAction>) => {
            if (data?.get('action') === 'existing') {
                onChooseExisting?.();
                return;
            }
            if (data?.get('action') === 'extra') {
                const extraId = data.get('extraId');
                extraItems?.find((item) => item.id === extraId)?.onSelect();
                return;
            }
            handleFileSelection();
        },
        [handleFileSelection, onChooseExisting, extraItems],
    );

    return (
        <div className={className}>
            <Dropdown
                myRef={dropdownRef}
                ToggleComponent={CircularToggleButton}
                toggleProps={Immutable.Map({ disabled, icon })}
                itemPropsList={itemPropsList}
                onSelect={handleSelect}
                // Cleared when the menu closes again, so the user's own next
                // click doesn't inherit the sweep. Guarded on having been open,
                // because Dropdown fires afterClose on mount too — unguarded,
                // that would clear the flag before the menu ever opened.
                afterOpen={() => {
                    hasOpenedRef.current = true;
                }}
                afterClose={() => {
                    if (hasOpenedRef.current) {
                        hasOpenedRef.current = false;
                        setSweepOnOpen(false);
                    }
                }}
            />

            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={acceptString}
                onChange={handleFileChange}
                style={{ display: 'none' }}
            />
        </div>
    );
};
